import type { SupabaseClient } from '@supabase/supabase-js';

export const SCOPED_ROSTER_EVIDENCE_FLAGS = [
  'scoped_roster_evidence_v1',
  'scoped_roster_evidence_enforce_v1',
] as const;

export function isTenantScopedRosterEvidenceFlag(key: string | null | undefined): boolean {
  return SCOPED_ROSTER_EVIDENCE_FLAGS.some((flag) => flag === key?.trim());
}

export type ScopedEvidenceMode = 'off' | 'shadow' | 'enforce';
export interface ScopedGateRun {
  mode: Exclude<ScopedEvidenceMode, 'off'>;
  runId: string;
  sourceRevision: number;
  expiresAt: string;
  period: Record<string, unknown>;
}

/** Tenant-only, default-off. Enforcement requires both explicit capabilities. */
export async function scopedEvidenceMode(
  client: SupabaseClient,
  tenantId: string,
): Promise<ScopedEvidenceMode> {
  const { data, error } = await client
    .from('feature_flags')
    .select('flag_key, enabled')
    .eq('tenant_id', tenantId)
    .in('flag_key', [...SCOPED_ROSTER_EVIDENCE_FLAGS]);
  // A failed rollout lookup retains the legacy path; SQL still enforces the
  // authoritative tenant mode and refuses an uncertified legacy gate set.
  if (error) return 'off';
  const enabled = new Set(
    (data ?? []).filter((row) => row.enabled === true).map((row) => row.flag_key),
  );
  if (!enabled.has('scoped_roster_evidence_v1')) return 'off';
  return enabled.has('scoped_roster_evidence_enforce_v1') ? 'enforce' : 'shadow';
}

export async function beginScopedGateRun(
  client: SupabaseClient,
  tenantId: string,
  periodId: string,
): Promise<ScopedGateRun> {
  const { data, error } = await client.rpc('begin_roster_scoped_gate_run_controlled', {
    p_tenant_id: tenantId,
    p_roster_period_id: periodId,
  });
  if (error) throw error;
  const result = data as Partial<ScopedGateRun> | null;
  if (
    !result ||
    !['shadow', 'enforce'].includes(result.mode ?? '') ||
    typeof result.runId !== 'string' ||
    !Number.isSafeInteger(result.sourceRevision) ||
    typeof result.expiresAt !== 'string' ||
    !result.period ||
    typeof result.period !== 'object'
  )
    throw new Error('roster_scoped_evidence_changed');
  return result as ScopedGateRun;
}

/** Readiness is advisory here. The controlled finaliser repeats it under locks. */
export async function getScopedGateEvidence(
  client: SupabaseClient,
  tenantId: string,
  periodId: string,
): Promise<{ valid: boolean; advisoryFresh: boolean }> {
  const { data, error } = await client.rpc('get_roster_scoped_gate_evidence_controlled', {
    p_tenant_id: tenantId,
    p_roster_period_id: periodId,
  });
  // An incompatible snapshot cannot certify reuse. The existing preparation
  // path performs a full refresh through fresh READ COMMITTED app RPCs.
  if (error?.code === '55000' && error.message === 'roster_scoped_evidence_requires_read_committed')
    return { valid: false, advisoryFresh: false };
  if (error) throw error;
  return {
    valid: data?.mode === 'enforce' && data?.valid === true,
    advisoryFresh: data?.advisoryFresh === true,
  };
}

export async function canReuseScopedGateRun(
  client: SupabaseClient,
  tenantId: string,
  periodId: string,
): Promise<boolean> {
  return (await getScopedGateEvidence(client, tenantId, periodId)).valid;
}

/** One refresh at most; a later race is returned for explicit manager review. */
export async function prepareScopedPublication(input: {
  mode: ScopedEvidenceMode;
  reusable: () => Promise<boolean>;
  refresh: () => Promise<{ success: boolean; error?: string }>;
}): Promise<{ success: boolean; error?: string; reused: boolean }> {
  if (input.mode === 'enforce' && (await input.reusable())) {
    return { success: true, reused: true };
  }
  const refreshed = await input.refresh();
  if (input.mode === 'enforce' && refreshed.success) {
    return { success: false, error: 'roster_scoped_evidence_refreshed', reused: false };
  }
  return { ...refreshed, reused: false };
}
